import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { useParams } from 'react-router-dom';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Table } from 'components/molecules/Table';
import { CardList } from 'components/molecules/CardList';
import { attachmentsTableColumns, Elabels, mapAttachmentsToDataSource } from '../../helpers';
import { AttachmentsTableCard } from '../AttachmentsTableCard';
import { useCase } from '../../hooks';
import { useDictionary } from 'models/dictionaryContext';
import { useLang } from 'models/langContext';
import { downloadUrlAsFile } from 'utils/files';
import AttachmentUploadDocumentsForm from './AttachmentUploadDocumentsForm';
import { findInDictionary } from 'helpers/dictionary';
import { FormModal } from 'components/molecules/Modal';
import { Grid } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';

interface IAttachments {
  isReadOnly?: boolean;
  isSupervisor?: boolean;
  isAssignUser?: boolean;
}

const Attachments = ({ isReadOnly, isSupervisor, isAssignUser }: IAttachments) => {
  const { t } = useTranslations();
  const { isDesktop } = useThemeBreakpoint();
  const { caseId = '' } = useParams<{ id: string; caseId: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [uploadDocumentName, setUploadDocumentName] = useState<string | null>(null);
  const [uploadDocumentId, setUploadDocumentId] = useState<string | null>(null);
  const [deleteDocumentId, setDeleteDocumentId] = useState<string | null>(null);
  const [uploadNewTypeDocument, setUploadNewTypeDocument] = useState(false);
  const {
    caseAttachmentsData,
    getCaseAttachmentsData,
    downloadCaseAttachmentFile,
    downloadCaseAttachmentFiles,
    deleteCaseAttachmentFile,
  } = useCase();
  const { selectedLanguage } = useLang();
  const { attachmentDocumentTypeData, getAttachmentDocumentTypeData } = useDictionary();

  useEffect(() => {
    if (!attachmentDocumentTypeData) {
      getAttachmentDocumentTypeData();
    }
  }, [selectedLanguage]);

  useEffect(() => {
    if (caseId) {
      (async function init() {
        setIsLoading(true);
        await getCaseAttachmentsData(caseId);
        setIsLoading(false);
      })();
    }
  }, [caseId]);

  const onReload = () => {
    getCaseAttachmentsData(caseId);
  };

  const downloadAll = async (typeUniqueId: string, name: string) => {
    const url = await downloadCaseAttachmentFiles(caseId, typeUniqueId);
    if (url) {
      downloadUrlAsFile(url, name);
    }
  };
  const uploadDocument = (uniqueId: string, documentName: string) => {
    setUploadDocumentName(documentName);
    setUploadDocumentId(uniqueId);
  };
  const downloadDocument = async (fileId: string, filename: string) => {
    const url = await downloadCaseAttachmentFile(fileId);
    if (url) {
      downloadUrlAsFile(url, filename);
    }
  };

  const deleteDocument = async () => {
    if (deleteDocumentId) {
      const res = await deleteCaseAttachmentFile(deleteDocumentId);
      setDeleteDocumentId(null);
      if (res) {
        onReload();
      }
    }
  };

  return (
    <>
      {uploadDocumentId && uploadDocumentName && (
        <AttachmentUploadDocumentsForm
          documentName={uploadDocumentName}
          caseId={caseId}
          uploadDocumentId={uploadDocumentId}
          onClose={() => {
            setUploadDocumentName(null);
            setUploadDocumentId(null);
          }}
          onReload={onReload}
        />
      )}
      {uploadNewTypeDocument && (
        <AttachmentUploadDocumentsForm
          caseId={caseId}
          onClose={() => {
            setUploadNewTypeDocument(false);
          }}
          onReload={onReload}
          isNewType
        />
      )}

      <FormModal
        open={!!deleteDocumentId}
        onClickClose={() => {
          setDeleteDocumentId(null);
        }}
        onClickApprove={() => {
          deleteDocument();
        }}
        approveButtonLabel="general.yes.label"
        cancelButtonLabel="general.no.label"
        title={t('caseManagement.cases.documentDelete.label')}
        text={t('caseManagement.cases.areYouSureDocumentDelete.text')}
      />

      {!isReadOnly && (isSupervisor || isAssignUser) && (
        <Grid container sx={{ justifyContent: 'end', alignItems: 'center', mb: '20px' }}>
          <Grid>
            <Button
              variant={EButtonVariants.contained}
              label={t(Elabels.addDocument)}
              size={EButtonSizes.small}
              icon={EIconTypes.plus}
              onClick={() => setUploadNewTypeDocument(true)}
              id="document_add-button"
            />
          </Grid>
        </Grid>
      )}

      {isDesktop ? (
        <Table
          columns={attachmentsTableColumns({
            downloadAll,
            uploadDocument,
            downloadDocument,
            deleteDocument: (id: string) => setDeleteDocumentId(id),
            t,
            isReadOnly,
            isSupervisor,
            isAssignUser,
          })}
          dataSource={mapAttachmentsToDataSource(
            caseAttachmentsData,
            attachmentDocumentTypeData,
          )}
          loader={isLoading}
          id="attachments_list-table"
        />
      ) : (
        <CardList
          items={mapAttachmentsToDataSource(caseAttachmentsData, attachmentDocumentTypeData)}
          render={(data, index) => (
            <AttachmentsTableCard
              data={data}
              downloadAll={downloadAll}
              uploadDocument={uploadDocument}
              downloadDocument={downloadDocument}
              deleteDocument={(id: string) => setDeleteDocumentId(id)}
              isReadOnly={isReadOnly}
              isSupervisor={isSupervisor}
              isAssignUser={isAssignUser}
              key={index}
            />
          )}
        />
      )}
    </>
  );
};

export default Attachments;
